const CAPACITY = 384;
const hash = (n) => {
  const v = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return v - Math.floor(v);
};
const finite = (p) => p && ['x', 'y', 'z'].every((axis) => Number.isFinite(p[axis]));

/** Shaded falling ribbons on the original wash triangles. This material never
 * displaces geometry: domain122 remains the sole wash/contact authority. The
 * caller owns disposal and supplies active encounter seconds, or zero for
 * reduced motion. Local bounds accommodate the source GLB's axis conversion. */
export function createSkyOrchidWaterMaterial(T, geometry) {
  geometry.computeBoundingBox();
  const size = geometry.boundingBox.getSize(new T.Vector3()).toArray();
  const axes = [0, 1, 2].sort((a, b) => size[b] - size[a]);
  const horizontal = new T.Vector3(),
    vertical = new T.Vector3();
  horizontal.setComponent(axes[1], 1 / size[axes[1]]);
  vertical.setComponent(axes[0], 1 / size[axes[0]]);
  return new T.ShaderMaterial({
    name: 'Source irrigation falling water',
    transparent: true,
    depthWrite: false,
    side: T.DoubleSide,
    uniforms: {
      waterTime: { value: 0 },
      horizontal: { value: horizontal },
      vertical: { value: vertical },
      origin: { value: geometry.boundingBox.min.clone() },
    },
    vertexShader: `
      uniform vec3 horizontal, vertical, origin;
      varying vec2 flowUv;
      varying vec3 worldPoint, worldNormal;
      void main() {
        flowUv=vec2(dot(position-origin,horizontal),dot(position-origin,vertical));
        vec4 world=modelMatrix*vec4(position,1.);
        worldPoint=world.xyz;
        worldNormal=normalize(mat3(modelMatrix)*normal);
        gl_Position=projectionMatrix*viewMatrix*world;
      }`,
    fragmentShader: `
      uniform float waterTime;
      varying vec2 flowUv;
      varying vec3 worldPoint, worldNormal;
      float hash(vec2 p) { return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453); }
      float noise(vec2 p) {
        vec2 i=floor(p), f=fract(p); f=f*f*(3.-2.*f);
        return mix(mix(hash(i),hash(i+vec2(1,0)),f.x),
                   mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),f.x),f.y);
      }
      void main() {
        vec2 p=flowUv;
        float time=waterTime;
        float broad=noise(vec2(p.x*17.,p.y*2.8+time*1.3));
        float ribbon=noise(vec2(p.x*86.+broad*3.,p.y*5.+time*2.8));
        float drops=noise(vec2(p.x*160.,p.y*27.+time*7.));
        float flow=smoothstep(.32,.78,ribbon)*(.45+.55*broad);
        float foam=smoothstep(.73,.96,drops)*flow;
        float edge=smoothstep(0.,.025,p.x)*smoothstep(0.,.025,1.-p.x);
        float fresnel=pow(1.-abs(dot(normalize(cameraPosition-worldPoint),
                                    normalize(worldNormal))),3.);
        vec3 colour=mix(vec3(.27,.45,.47),vec3(.78,.89,.87),flow*.65+foam*.3);
        float alpha=edge*(.045+flow*.32+foam*.2+fresnel*.1);
        gl_FragColor=vec4(colour,alpha);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
  });
}

/** Bounded pollen and irrigation spray follow canonical encounter geometry and
 * active simulation time. Waterfall solids remain the original GLB; these soft
 * droplets neither invent collision nor advance pollen/seed state. */
export function createSkyOrchidEffects(T, scene, own, reduced = () => false) {
  const geometry = own(new T.InstancedBufferGeometry());
  geometry.setAttribute(
    'position',
    new T.Float32BufferAttribute([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0], 3),
  );
  geometry.setAttribute('uv', new T.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  const offset = new T.InstancedBufferAttribute(new Float32Array(CAPACITY * 3), 3),
    appearance = new T.InstancedBufferAttribute(new Float32Array(CAPACITY * 3), 3);
  offset.setUsage(T.DynamicDrawUsage);
  appearance.setUsage(T.DynamicDrawUsage);
  geometry.setAttribute('particleOffset', offset);
  geometry.setAttribute('particleAppearance', appearance);
  geometry.instanceCount = 0;
  const material = own(
    new T.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: true,
      toneMapped: true,
      vertexShader: `
      attribute vec3 particleOffset; attribute vec3 particleAppearance;
      varying vec2 puffUv; varying vec2 puffStyle;
      void main(){puffUv=uv*2.0-1.0;puffStyle=particleAppearance.yz;
        vec4 view=modelViewMatrix*vec4(particleOffset,1.0);view.xy+=position.xy*particleAppearance.x;
        gl_Position=projectionMatrix*view;}`,
      fragmentShader: `
      varying vec2 puffUv; varying vec2 puffStyle;
      void main(){float edge=1.0-smoothstep(.12,1.0,length(puffUv));
        float alpha=edge*edge*puffStyle.x;if(alpha<.005)discard;
        vec3 pollen=mix(vec3(.28,.72,.08),vec3(.94,.84,.24),.4+sin(puffUv.x*8.0)*.15);
        vec3 color=mix(pollen,vec3(.48,.85,.94),puffStyle.y);
        gl_FragColor=vec4(color,alpha);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`,
    }),
  );
  const mesh = new T.Mesh(geometry, material);
  mesh.name = 'Orchid magnetic pollen and source irrigation spray';
  mesh.frustumCulled = false;
  mesh.visible = false;
  scene.add(mesh);
  let count = 0,
    clouds = 0,
    waterfalls = 0,
    attached = false;
  function particle(x, y, z, size, alpha, water) {
    if (count >= CAPACITY || ![x, y, z, size, alpha].every(Number.isFinite)) return;
    offset.setXYZ(count, x, y, z);
    appearance.setXYZ(count, size, alpha, water);
    count++;
  }
  function draw(state, visible = true) {
    count = clouds = waterfalls = 0;
    attached = false;
    const boss = state?.boss,
      o = boss?.orchid;
    if (!visible || !o || boss.defeated) {
      geometry.instanceCount = 0;
      mesh.visible = false;
      return;
    }
    const quiet = reduced(),
      time = Number.isFinite(state.time) ? state.time : 0;
    for (const cloud of (o.clouds ?? []).slice(0, 2)) {
      if (!finite(cloud.head) || !Number.isFinite(cloud.radius) || cloud.radius <= 0) continue;
      clouds++;
      const amount = quiet ? 24 : 64;
      for (let i = 0; i < amount; i++) {
        const seed = (cloud.id ?? 0) * 79 + i,
          theta = hash(seed) * Math.PI * 2 + (quiet ? 0 : cloud.age * 0.22),
          vertical = hash(seed + 1) * 2 - 1;
        const radial = Math.sqrt(1 - vertical * vertical),
          r = Math.cbrt(hash(seed + 2)) * Math.min(4, cloud.radius);
        particle(
          cloud.head.x + Math.cos(theta) * radial * r,
          cloud.head.y + vertical * r,
          cloud.head.z + Math.sin(theta) * radial * r,
          quiet ? 0.7 : 1.05,
          0.42,
          0,
        );
      }
    }
    if (o.pollen && finite({ x: state.x, y: state.y, z: state.pilotZ ?? 1 })) {
      attached = true;
      const amount = quiet ? 12 : 24;
      for (let i = 0; i < amount; i++) {
        const a = hash(i) * Math.PI * 2 + (quiet ? 0 : time * 0.35),
          r = 0.35 + hash(i + 20) * 0.4;
        particle(
          state.x + Math.cos(a) * r,
          state.y + (hash(i + 5) - 0.5) * 1.1,
          (state.pilotZ ?? 1) + Math.sin(a) * r,
          0.22,
          0.7,
          0,
        );
      }
    }
    for (const water of (o.waterfalls ?? []).slice(0, 2)) {
      if (!Array.isArray(water.triangles) || !water.triangles.length) continue;
      waterfalls++;
      const amount = quiet ? 32 : 80,
        triangles = water.triangles;
      for (let i = 0; i < amount; i++) {
        // Falling rows traverse the original192 curtain facets. Barycentric
        // samples remain on their source surface, including its shallow waves.
        const phase = (hash(i + water.id) + time * 0.6) % 1;
        const triangle =
          triangles[Math.min(triangles.length - 1, Math.floor((1 - phase) * triangles.length))];
        if (!triangle || ![triangle.a, triangle.b, triangle.c].every(finite)) continue;
        const u = Math.sqrt(hash(i + 90)),
          v = hash(i + 210),
          weights = [1 - u, u * (1 - v), u * v];
        const p = ['x', 'y', 'z'].map(
          (axis) =>
            weights[0] * triangle.a[axis] +
            weights[1] * triangle.b[axis] +
            weights[2] * triangle.c[axis],
        );
        particle(...p, quiet ? 0.28 : 0.45, 0.58, 1);
      }
    }
    geometry.instanceCount = count;
    mesh.visible = count > 0;
    offset.needsUpdate = appearance.needsUpdate = true;
  }
  return {
    draw,
    diagnostics: () => ({ particles: count, clouds, waterfalls, attached, capacity: CAPACITY }),
  };
}
